/**
 * Zone Service - API client for zone management
 *
 * Handles CRUD operations for network zones.
 * Zone management endpoints do NOT include zone prefix in the URL.
 */
import { networkApi } from '../infrastructure/api/NetworkApiClient';
import type { NetworkZone, CreateZoneRequest, UpdateZoneRequest, ZoneStatisticsSummary } from '../types/zone';

class ZoneService {
  /**
   * Get all network zones
   */
  async getAllZones(activeOnly = false): Promise<NetworkZone[]> {
    const url = activeOnly ? '/zones?activeOnly=true' : '/zones';
    return networkApi.httpGet<NetworkZone[]>(url);
  }

  /**
   * Get a specific zone by ID
   */
  async getZone(zoneId: string): Promise<NetworkZone> {
    return networkApi.httpGet<NetworkZone>(`/zones/${zoneId}`);
  }

  /**
   * Create a new zone
   */
  async createZone(data: CreateZoneRequest): Promise<NetworkZone> {
    return networkApi.httpPost<NetworkZone>('/zones', data);
  }

  /**
   * Update an existing zone
   */
  async updateZone(zoneId: string, data: UpdateZoneRequest): Promise<NetworkZone> {
    return networkApi.httpPut<NetworkZone>(`/zones/${zoneId}`, data);
  }

  /**
   * Delete a zone
   */
  async deleteZone(zoneId: string): Promise<void> {
    return networkApi.httpDelete(`/zones/${zoneId}`);
  }

  /**
   * Import a GeoJSON polygon for a zone
   */
  async importPolygon(zoneId: string, polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon): Promise<NetworkZone> {
    return networkApi.httpPut<NetworkZone>(`/zones/${zoneId}/polygon`, polygon);
  }

  /**
   * Get statistics summaries for all zones (landing page)
   */
  async getZoneStatistics(): Promise<ZoneStatisticsSummary[]> {
    // The dashboard and the map's zone-polygon tooltips both ask for the summary of all
    // zones at startup; one ~100 KB response per few seconds is plenty (the dashboard
    // refreshes every 60 s anyway).
    const now = Date.now();
    if (this.statisticsCache && now - this.statisticsCache.at < ZoneService.StatisticsCacheMs) {
      return this.statisticsCache.promise;
    }
    const promise = networkApi.httpGet<ZoneStatisticsSummary[]>('/zones/statistics');
    this.statisticsCache = { at: now, promise };
    promise.catch(() => { this.statisticsCache = null; });
    return promise;
  }

  private static readonly StatisticsCacheMs = 5000;
  private statisticsCache: { at: number; promise: Promise<ZoneStatisticsSummary[]> } | null = null;
}

export const zoneService = new ZoneService();
